/**
 * Guide progress helpers — keep chapter index in sync with completed milestones
 * and detect when the user has navigated away from the expected screen.
 */

/** Event that completes a chapter (advanceOn) → that chapter is done. */
export const EVENT_COMPLETES_CHAPTER = {
  "anchor-continued": "create-app",
  "method-picked": "choose-how",
  "job-queued": "supply",
  "job-ready": "processing",
  "review-complete": "review",
};

/** Soft wizard signals → inferred "at least this far" (catch-up, not always advance). */
export const WIZARD_STEP_CHAPTER = {
  scope: "create-app",
  anchor: "create-app",
  pick: "choose-how",
  source: "supply",
};

/** Add-knowledge wizard order (earlier steps lock once the guide advances). */
export const WIZARD_STEP_ORDER = ["scope", "anchor", "pick", "source"];

export function wizardStepIndex(step) {
  const idx = WIZARD_STEP_ORDER.indexOf(String(step || "").trim());
  return idx < 0 ? -1 : idx;
}

/** Minimum wizard step the UI should hold for this guide chapter. */
export function expectedWizardStepForChapter(chapterId) {
  const expected = expectedLocationForChapter(chapterId);
  return expected?.wizard || null;
}

/**
 * True when `step` is before the guide's expected wizard step
 * (e.g. scope while guide is on create-app/anchor).
 */
export function isWizardStepBeforeGuide(step, chapterId) {
  const want = expectedWizardStepForChapter(chapterId);
  if (!want) return false;
  const cur = wizardStepIndex(step);
  const min = wizardStepIndex(want);
  if (cur < 0 || min < 0) return false;
  return cur < min;
}

/** Chapters whose Add-knowledge wizard may run on Home (desktop) or Map (mobile). */
const ADD_KNOWLEDGE_CHAPTERS = new Set([
  "create-app",
  "choose-how",
  "supply",
]);

/** Soft-focus chapters: stray nav is intercepted and the user is snapped back. */
export const GUIDE_FOCUS_CHAPTERS = new Set([
  "intent",
  "create-app",
  "choose-how",
  "supply",
  "processing",
  "review",
]);

/** Routes allowed while Building (processing) — not free browsing. */
const PROCESSING_ALLOWED_ROUTES = new Set([
  "home",
  "add-knowledge",
  "insights-inbox",
]);

/**
 * Shell journey rail — collapses guide chapters into four orientation steps.
 * GuideStrip still coaches; this answers “where am I in the loop?”
 */
export const JOURNEY_STEPS = [
  { id: "add", label: "Add", entryChapterId: "create-app" },
  { id: "building", label: "Building", entryChapterId: "processing" },
  { id: "review", label: "Review", entryChapterId: "review" },
  { id: "ask", label: "Ask", entryChapterId: "win" },
];

/** Guide chapter id → journey step id. */
export const CHAPTER_TO_JOURNEY_STEP = {
  intent: "add",
  "create-app": "add",
  "choose-how": "add",
  supply: "add",
  processing: "building",
  review: "review",
  win: "ask",
};

export function journeyStepIdForChapter(chapterId) {
  return CHAPTER_TO_JOURNEY_STEP[String(chapterId || "").trim()] || "add";
}

export function journeyStepIndex(stepId) {
  const idx = JOURNEY_STEPS.findIndex((s) => s.id === stepId);
  return idx < 0 ? 0 : idx;
}

/**
 * Status for each rail step given the active guide chapter.
 * @returns {{ id: string, label: string, entryChapterId: string, status: 'done'|'current'|'upcoming' }[]}
 */
export function journeyStepsForChapter(chapterId) {
  const currentId = journeyStepIdForChapter(chapterId);
  const currentIdx = journeyStepIndex(currentId);
  return JOURNEY_STEPS.map((step, idx) => ({
    ...step,
    status: idx < currentIdx ? "done" : idx === currentIdx ? "current" : "upcoming",
  }));
}

/** First-run CPH sample script — only this path shows a Building ETA. */
export const CPH_SAMPLE_SCRIPT_ID = "add-knowledge-first-app";

/**
 * Typical wall time for Customer Payments Hub sample extract (local + prod).
 * Midpoint of the observed ~2–3 minute range.
 */
export const CPH_PROCESSING_ETA_MS = 150_000;

export function isCphSampleGuide(script) {
  if (!script) return false;
  if (String(script.id || "") === CPH_SAMPLE_SCRIPT_ID) return true;
  return /customer\s*payments\s*hub/i.test(String(script.sampleName || ""));
}

/**
 * Live countdown label for CPH Building. Returns null when not applicable.
 * @param {number|null|undefined} startedAtMs
 * @param {number} [nowMs]
 * @param {number} [etaMs]
 */
export function formatCphProcessingEta(
  startedAtMs,
  nowMs = Date.now(),
  etaMs = CPH_PROCESSING_ETA_MS
) {
  const started = Number(startedAtMs);
  if (!Number.isFinite(started) || started <= 0) return null;
  const remainingMs = Math.max(0, started + etaMs - nowMs);
  if (remainingMs <= 0) {
    return "Usually wraps up soon…";
  }
  const totalSec = Math.ceil(remainingMs / 1000);
  const mins = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  if (mins <= 0) {
    return `About ${totalSec}s left`;
  }
  if (secs === 0) {
    return `About ${mins} min left`;
  }
  return `About ${mins}m ${secs}s left`;
}

export function chapterIndexOf(script, chapterId) {
  if (!script?.chapters?.length) return 0;
  const idx = script.chapters.findIndex((c) => c.id === chapterId);
  return idx < 0 ? 0 : idx;
}

/**
 * Mark chapters through `completedChapterId` done; set chapterId to the next one.
 */
export function applyChapterCompletion(script, state, completedChapterId) {
  if (!script?.chapters?.length || !completedChapterId) return state || null;
  const idx = chapterIndexOf(script, completedChapterId);
  const done = new Set(
    Array.isArray(state?.milestonesCompleted) ? state.milestonesCompleted : []
  );
  for (let i = 0; i <= idx; i += 1) {
    const id = script.chapters[i]?.id;
    if (id) done.add(id);
  }
  // Also mark intent done once we've left the start.
  if (completedChapterId !== "intent") done.add("intent");

  const next = script.chapters[idx + 1];
  const chapterId = next?.id || script.chapters[idx]?.id || state?.chapterId;
  return {
    ...(state || {}),
    milestonesCompleted: Array.from(done),
    chapterId,
  };
}

/**
 * Apply a product guide event. Catch-up: completing a late step also marks priors done.
 */
export function applyGuideEvent(script, state, eventType) {
  const completedId = EVENT_COMPLETES_CHAPTER[String(eventType || "").trim()];
  if (!completedId) return state;
  return applyChapterCompletion(script, state, completedId);
}

/**
 * Infer catch-up from Add-knowledge wizard step while tour is active.
 * Only moves forward.
 */
export function catchUpFromWizardStep(script, state, wizardStep) {
  const inferred = WIZARD_STEP_CHAPTER[String(wizardStep || "").trim()];
  if (!inferred || !script) return state;
  const currentIdx = chapterIndexOf(script, state?.chapterId);
  const inferredIdx = chapterIndexOf(script, inferred);
  // Being ON create-app/choose-how/supply does not complete them — only
  // positions the strip if the user jumped ahead past intent.
  if (inferredIdx <= currentIdx) return state;
  // Jump strip forward to the wizard's chapter (without marking prior advanceOn done).
  return {
    ...(state || {}),
    chapterId: inferred,
  };
}

/**
 * Keep guide chapter aligned with the live wizard step (both directions)
 * while still in the add-knowledge arc — prevents strip tips from racing ahead.
 */
export function alignChapterToWizardStep(script, state, wizardStep) {
  const step = String(wizardStep || "").trim();
  const inferred = WIZARD_STEP_CHAPTER[step];
  if (!inferred || !script || !state?.scriptId) return state;
  const chapter = String(state.chapterId || "");
  // Don't pull processing / review / win back into the wizard arc.
  if (
    chapter &&
    !ADD_KNOWLEDGE_CHAPTERS.has(chapter) &&
    chapter !== "intent"
  ) {
    return state;
  }
  if (chapter === inferred) {
    return state?.wizardStep === step ? state : { ...state, wizardStep: step };
  }
  return {
    ...state,
    chapterId: inferred,
    wizardStep: step,
  };
}

/** Where the user should be for the current chapter. */
export function expectedLocationForChapter(chapterId) {
  switch (String(chapterId || "")) {
    case "intent":
      // Ask welcome — entry is “Try an example”, not a strip Begin button.
      return { route: "home", panel: null, label: "Ask", allowHomeAsk: true };
    case "create-app":
      return {
        route: "add-knowledge",
        panel: null,
        wizard: "anchor",
        label: "Add knowledge",
        allowMapAdd: true,
      };
    case "choose-how":
      return {
        route: "add-knowledge",
        panel: null,
        wizard: "pick",
        label: "Add knowledge",
        allowMapAdd: true,
      };
    case "supply":
      return {
        route: "add-knowledge",
        panel: null,
        wizard: "source",
        label: "Add knowledge",
        allowMapAdd: true,
      };
    case "processing":
      // Soft-locked: Inbox / Add / Ask / Map only — not free browsing.
      return {
        route: "insights-inbox",
        panel: null,
        label: "Inbox",
        allowProcessingWait: true,
      };
    case "review":
      return { route: "insights-inbox", panel: null, label: "Inbox" };
    case "win":
      // Land on the Ask agent (Home), not the map gallery.
      return {
        route: "home",
        panel: "ask",
        label: "Ask",
        allowHomeAsk: true,
      };
    default:
      return { route: null, panel: null, label: null, allowAnywhere: true };
  }
}

/** True while the guide should keep the user on the expected screen. */
export function isGuideFocusChapter(chapterId) {
  return GUIDE_FOCUS_CHAPTERS.has(String(chapterId || ""));
}

/**
 * Whether navigating to `targetRoute` is allowed during Soft focus.
 * Map (architecture-gallery) is locked — guided add uses Home / Add knowledge only.
 * Review stays on Inbox.
 */
export function isGuideNavAllowed({ chapterId, targetRoute } = {}) {
  const chapter = String(chapterId || "");
  if (!isGuideFocusChapter(chapter)) return true;
  const route = String(targetRoute || "").trim();
  if (!route) return true;
  const expected = expectedLocationForChapter(chapter);
  if (expected?.allowAnywhere) return true;
  if (chapter === "intent") {
    return route === "home";
  }
  if (ADD_KNOWLEDGE_CHAPTERS.has(chapter)) {
    // Stay on Add knowledge — not Ask/Home (that was dropping wizard context).
    return route === "add-knowledge";
  }
  if (chapter === "processing") {
    return PROCESSING_ALLOWED_ROUTES.has(route);
  }
  if (chapter === "review") {
    return route === "insights-inbox";
  }
  return !expected?.route || route === expected.route;
}

/**
 * True when the strip primary CTA would be redundant (user already there).
 * Intent has no strip primary — the Ask “Try an example” chip is the CTA.
 */
export function isGuidePrimarySatisfied(
  chapterId,
  {
    currentRoute,
    homePanel,
    sampleNotesLoaded = false,
    wizardStep = "",
  } = {}
) {
  const chapter = String(chapterId || "");
  const route = String(currentRoute || "").trim();
  const step = String(wizardStep || "").trim();
  // In-page CTAs own these steps — strip Load sample / etc. stays hidden.
  if (
    step === "scope" ||
    step === "anchor" ||
    step === "pick" ||
    step === "source"
  ) {
    return true;
  }
  switch (chapter) {
    case "intent":
      return true;
    case "supply":
      return Boolean(sampleNotesLoaded);
    case "processing":
      return route === "insights-inbox";
    case "review":
      return route === "insights-inbox";
    case "win":
      return route === "home" && String(homePanel || "") !== "add";
    default:
      return false;
  }
}

/**
 * Strip tip from live wizard UI state first, then chapter fallbacks.
 * `wizardUi`: { step, hasAnchor, hasNotes } from SystemInput.
 */
export function resolveGuideStripTip(
  chapter,
  {
    currentRoute,
    homePanel,
    isMobile = false,
    offPath = false,
    sampleNotesLoaded = false,
    wizardUi = null,
    entityId = "",
  } = {}
) {
  if (offPath) {
    return "Guided demo — that area is locked. Continue on this step.";
  }
  const chapterId = String(chapter?.id || "");
  const route = String(currentRoute || "").trim();
  const hasAnchor = Boolean(
    wizardUi?.hasAnchor || String(entityId || "").trim()
  );
  const hasNotes = Boolean(wizardUi?.hasNotes || sampleNotesLoaded);

  // After Extract is queued, ignore stale wizardUi (often still "source") —
  // otherwise the tip keeps saying "click Add knowledge" while Open Inbox blinks.
  if (chapterId === "processing") {
    if (route === "insights-inbox") {
      return "Extract still running — when it finishes, open Review on your upload.";
    }
    return isMobile
      ? chapter?.tipMobile || chapter?.tip || null
      : chapter?.tip || null;
  }
  if (chapterId === "review") {
    // Live card in the review dialog — coach the decision CTA.
    if (String(wizardUi?.step || "") === "review-decide") {
      return isMobile
        ? "Tap Add to map on this suggestion (or Link / Skip)."
        : "Click Add to map on this suggestion (or Link / Skip).";
    }
    if (route === "insights-inbox") {
      return "Open Review on your upload — approve, link, or skip each suggestion.";
    }
    return isMobile
      ? chapter?.tipMobile || chapter?.tip || null
      : chapter?.tip || null;
  }
  if (chapterId === "win") {
    if (route === "home" && String(homePanel || "") !== "add") {
      return "Ask about Customer Payments Hub using the chips below.";
    }
    return isMobile
      ? chapter?.tipMobile || chapter?.tip || null
      : chapter?.tip || null;
  }

  // Prefer live wizard step only while still in the Add-knowledge arc.
  const step = String(
    wizardUi?.step || expectedWizardStepForChapter(chapterId) || ""
  ).trim();

  // Live Add-knowledge wizard — always prefer this over chapter copy.
  if (step === "scope") {
    return isMobile
      ? "Tap Application to choose what you’re adding."
      : "Choose Application to continue.";
  }
  if (step === "pick") {
    return isMobile
      ? "Tap Paste or type for the quickest first run."
      : "Click the Paste or type card for the quickest first run.";
  }
  if (step === "source") {
    if (hasNotes) {
      return isMobile
        ? "Notes are ready — tap Add knowledge / Extract."
        : "Notes are ready — click Add knowledge / Extract.";
    }
    return isMobile
      ? "Paste or load notes, then tap Add knowledge."
      : "Paste or load notes, then click Add knowledge / Extract.";
  }
  // Create step: live "anchor" or create-app chapter before wizard UI reports.
  if (step === "anchor" || chapterId === "create-app") {
    if (!hasAnchor) {
      return isMobile
        ? "Tap Create entity to add Customer Payments Hub."
        : "Click Create entity to add Customer Payments Hub.";
    }
    return isMobile
      ? "Entity ready — tap Continue to choose how."
      : "Entity ready — click Continue to choose how →";
  }

  if (isMobile && chapter?.tipMobile) return chapter.tipMobile;
  return chapter?.tip || null;
}

/**
 * True when the strip chapter expects a specific screen and the user is elsewhere.
 * Mobile Add knowledge runs on Map (`architecture-gallery` + mapAddOpen).
 */
export function isGuideOffPath({
  chapterId,
  currentRoute,
  homePanel,
  mapAddOpen = false,
} = {}) {
  const expected = expectedLocationForChapter(chapterId);
  if (!expected || expected.allowAnywhere || !expected.route) return false;
  const route = String(currentRoute || "").trim();
  const chapter = String(chapterId || "");

  // Intent / Ask win: stay on Home (Ask agent).
  if (chapter === "intent" || chapter === "win") {
    return Boolean(route && route !== "home");
  }

  // Building: locked to a small set of routes (not free browse).
  if (chapter === "processing" || expected.allowProcessingWait) {
    if (!route) return false;
    return !PROCESSING_ALLOWED_ROUTES.has(route);
  }

  // Add-knowledge chapters: Home add panel (desktop) OR Map add panel (mobile).
  if (expected.allowMapAdd || ADD_KNOWLEDGE_CHAPTERS.has(chapter)) {
    if (route === "add-knowledge") return false;
    if (route === "home" && String(homePanel || "") === "add") return false;
    if (route === "architecture-gallery" && mapAddOpen) return false;
    return true;
  }

  if (route && route !== expected.route) return true;
  if (
    expected.route === "home" &&
    expected.panel &&
    homePanel &&
    String(homePanel) !== String(expected.panel)
  ) {
    return true;
  }
  return false;
}
